import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, CheckCircle, XCircle, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

import { webhookApi } from '../lib/api/webhookApi';
import { WebhookSummary, WebhookListResponse } from '../lib/api/types';

interface WebhookListProps {
  partnerId: string;
  onWebhookSelect: (webhookId: string) => void;
  liveMonitoring?: boolean;
}

export const WebhookList: React.FC<WebhookListProps> = ({ partnerId, onWebhookSelect, liveMonitoring = false }) => {

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('');

  // Reset page to 0 when filter changes
  useEffect(() => {
    setCurrentPage(0);
  }, [eventTypeFilter]);

  const {
    data: webhookData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['webhooks', 'list', partnerId, currentPage, pageSize, eventTypeFilter],
    queryFn: () => webhookApi.listWebhooks(partnerId, {
      offset: currentPage * pageSize,
      limit: pageSize,
      eventType: eventTypeFilter || undefined,
    }),
    enabled: !!partnerId,
    refetchInterval: liveMonitoring ? 2000 : false,
  });

  // Separate query to get all webhooks for extracting unique event types (for filter dropdown)
  const allWebhooksQuery = useQuery({
    queryKey: ['webhooks', 'all-event-types', partnerId],
    queryFn: () => webhookApi.listWebhooks(partnerId, {
      offset: 0,
      limit: 100, // API maximum limit
      // No eventType filter to get all webhooks
    }),
    enabled: !!partnerId,
    refetchInterval: liveMonitoring ? 2000 : false,
  });

  const allWebhooksData = allWebhooksQuery.data;

  // Extract unique event types from ALL webhook data (must be before conditional returns)
  const allWebhooks = allWebhooksData?.webhooks || [];

  // Filter webhooks by event type on the client side
  const filteredWebhooks = useMemo(() => {
    if (!eventTypeFilter || eventTypeFilter === '') {
      return allWebhooks;
    }
    return allWebhooks.filter((webhook: WebhookSummary) =>
      webhook.eventType && webhook.eventType.toLowerCase().includes(eventTypeFilter.toLowerCase())
    );
  }, [allWebhooks, eventTypeFilter]);

  const uniqueEventTypes = useMemo(() => {
    if (!allWebhooks || allWebhooks.length === 0) {
      return [];
    }

    const eventTypes = new Set<string>();
    allWebhooks.forEach((webhook: WebhookSummary) => {
      if (webhook.eventType && webhook.eventType.trim()) {
        eventTypes.add(webhook.eventType.trim());
      }
    });

    return Array.from(eventTypes).sort();
  }, [allWebhooks]);

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    if (webhookData?.pagination?.hasMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Webhook History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading webhooks...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Webhook History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-destructive">Failed to load webhooks</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalCount = webhookData?.totalCount || 0;
  const pagination = webhookData?.pagination;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Webhook History ({totalCount})</CardTitle>
          <div className="flex items-center gap-2">
            <select
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              className="w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <option value="">All Events</option>
              {uniqueEventTypes.map((eventType) => (
                <option key={eventType} value={eventType}>
                  {eventType}
                </option>
              ))}
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredWebhooks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {eventTypeFilter ? `No webhooks found with event type "${eventTypeFilter}"` : 'No webhooks found for this partner'}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredWebhooks.map((webhook: WebhookSummary) => (
              <div
                key={webhook.webhookId}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => onWebhookSelect(webhook.webhookId)}
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(webhook.status)}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${webhook.signatureVerified ? 'text-green-600' : 'text-red-600'}`}>
                        {webhook.signatureVerified ? 'Signature Verified' : 'Signature Not Verified'}
                      </span>
                      <span className="font-medium text-sm">{webhook.eventType}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      ID: {webhook.webhookId}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm">{formatDate(webhook.receivedAt)}</div>
                  {webhook.processingTime && (
                    <div className="text-xs text-muted-foreground">
                      {webhook.processingTime}ms
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalCount > pageSize && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount} webhooks
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={currentPage === 0}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={!pagination?.hasMore}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
